/**
 * Shape of a compact icon action. Applied to the button element itself, which a menu trigger
 * requires — `matMenuTriggerFor` has to sit on the button, not on a wrapper.
 *
 * Fades while disabled, at the same strength {@link ToggleDirective} uses: these are held back
 * whenever a write is in flight on the record, and a control that stops answering without saying so
 * reads as a broken page rather than a busy one.
 */
const ICON_BUTTON_BASE =
  'inline-flex size-9 items-center justify-center rounded-full text-neutral-400 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none sm:size-6';

export const ICON_BUTTON_CLASS = `${ICON_BUTTON_BASE} hover:bg-neutral-100 hover:text-neutral-700`;

/**
 * The same shape for an icon action that takes something away — unfiling, detaching. Only the hover
 * differs: at rest a destructive control must not be the loudest thing in a row it merely sits in,
 * and red at rest is already spoken for by the states.
 */
export const ICON_BUTTON_DANGER_CLASS = `${ICON_BUTTON_BASE} hover:bg-error-50 hover:text-error-700`;

/** Names a block of facts. Same weight as {@link FactComponent}'s own label, one step up. */
export const SECTION_LABEL_CLASS = 'text-xs leading-4 font-medium tracking-wide text-neutral-400 uppercase';

const BUTTON_BASE =
  'inline-flex items-center gap-1 rounded-full text-sm font-semibold transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none';

/** `md` commits a dialog; `sm` is the same button standing inside a panel row, where it is one of many. */
const BUTTON_SIZE = { sm: 'px-2 py-1', md: 'px-3 py-1.5' } as const;

const BUTTON_TONE = {
  /** The one act a screen is for. At most one per surface, or none of them reads as the commitment. */
  primary: 'bg-primary-600 text-neutral-0 hover:bg-primary-700',
  /** The same act offered inside a panel, where a filled pill would outshout the record beside it. */
  soft: 'bg-primary-50 text-primary-700 hover:bg-primary-100',
  /** A step out of the way — widening a window, reopening what was closed. */
  outline: 'border border-neutral-200 text-neutral-600 hover:bg-neutral-50',
  /** Dismissal, and anything that changes nothing. */
  ghost: 'text-neutral-500 hover:bg-neutral-100',
  /** Reports a state as well as offering to drop it — an applied filter, a term being searched for. */
  neutral: 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
  /** What cannot be undone by pressing it again. Never filled: red is spoken for by the states. */
  danger: 'text-error-700 hover:bg-error-50',
} as const;

/**
 * One button vocabulary for the module, so a control means the same thing wherever it stands: tone
 * says what kind of act it is, size says how much of the screen the act owns. Hand-rolling a pill
 * beside these puts a second answer on the page to a question that already has one.
 *
 * Icon-only controls are {@link ICON_BUTTON_CLASS}; a control that reports a state as well as
 * offering an act is a `ChipComponent` with `variant="action"`.
 */
export const BUTTON_CLASSES = Object.fromEntries(
  Object.entries(BUTTON_TONE).map(([tone, toneClass]) => [tone, { sm: `${BUTTON_BASE} ${BUTTON_SIZE.sm} ${toneClass}`, md: `${BUTTON_BASE} ${BUTTON_SIZE.md} ${toneClass}` }])
) as Record<keyof typeof BUTTON_TONE, { sm: string; md: string }>;

const MENU_HINT_SHAPE = 'mt-0.5 block text-xs leading-4 font-normal';

/**
 * Applied through `<mat-menu class="…">`, which forwards to the overlay panel, so nothing overrides
 * Material app-wide. `!` wherever Material declares the same property: its panel and item rules are
 * single-class, so a plain utility wins or loses on load order.
 */
export const MENU_CLASSES = {
  /**
   * The overlay panel. Material caps it at 280px, which truncates the names it exists to show —
   * and its structural `overflow: auto` turns anything wider into a horizontal scrollbar, so
   * sideways overflow is cut off here and the label class below wraps instead.
   */
  panel: 'max-w-88! overflow-x-hidden! rounded-xl! border border-neutral-200 bg-neutral-0! p-1',
  /** One row, sized to the rows that opened it rather than to Material's 48px default. */
  item: 'min-h-8! rounded-lg! px-2!',
  /** The icon leading a row, at the size the stream reads icons at. */
  icon: 'mr-2! h-4! w-4! text-base! leading-4!',
  /**
   * A row's label. Set on the projected span: the item's own text element is Material's to size.
   * Wraps rather than widening the row — a long name must never scroll the panel sideways.
   */
  label: 'min-w-0 text-sm font-medium whitespace-normal',
  /**
   * A refusal under a row's own label, saying why the act it names cannot be taken as things stand.
   * Sits inside the label so it wraps with it rather than widening the panel.
   */
  hint: `${MENU_HINT_SHAPE} text-neutral-400`,
  /**
   * The same line without a colour, for a row whose hint carries one of its own. Appending a second
   * colour to {@link MENU_CLASSES.hint} would not override it: two `text-*` utilities on one element
   * are settled by their order in the generated stylesheet, not by their order in the attribute.
   */
  hintShape: MENU_HINT_SHAPE,
  /** A heading over a group of rows. */
  heading: 'block px-2 py-1 text-sm font-semibold text-neutral-400',
  /** What a dropdown says when it has nothing to offer. */
  empty: 'block px-2 py-1 text-sm text-neutral-400',
} as const;

/**
 * The shape every dialog in this module takes: a heading that stays put, a body that scrolls, and
 * the acts pinned to the bottom.
 *
 * Split into three rather than laid out as one padded column because these forms ask their
 * questions as the earlier ones are answered — grounds appear once a ruling is given, a course once
 * the kind is a drug. In one column each of those walks the submit button down the screen and
 * resizes the box under the reader's cursor; here the box is capped and only the body moves.
 */
export const DIALOG_CLASSES = {
  /**
   * Capped at whatever the dialog was opened with, which the container and the surface both pass
   * down as `inherit` — so the body below is the only thing that ever scrolls.
   */
  shell: 'flex max-h-[inherit] w-full min-w-0 flex-col',
  header: 'flex shrink-0 flex-col gap-0.5 px-4 pt-4 pb-3',
  /** `min-h-0` is load-bearing: a flex child refuses to shrink below its content without it. */
  body: 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-1',
  footer: 'flex shrink-0 flex-wrap items-center gap-1.5 border-t border-neutral-100 px-4 py-3',
} as const;

/**
 * What a field is still owed, under the control that owes it. Holds its line whether or not it has
 * anything to say: a message that appears and disappears as a form is filled in reflows everything
 * under it, which is the same fault the dialog shell above exists to avoid.
 */
export const FIELD_MESSAGE_CLASS = 'min-h-5 text-sm text-error-600';
